/**
 * Accès au domaine rapports.
 *
 * `POST .../generer` répond `202` avec `statut: en_cours` (D30, génération asynchrone). L'écran doit
 * donc interroger `GET /v1/rapports/{id}` jusqu'à `pret`, ce que `refetchInterval` fait tant que le
 * statut n'est pas terminal — s'arrêter dès `pret` ou `echec` évite de solliciter le serveur pour rien
 * une fois le résultat connu.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../shared/api/client";
import type { DemandeRapport, ModeleExportSelectionnable, Rapport } from "../../shared/api/types";

export function useRapports() {
  return useQuery({
    queryKey: ["rapports"],
    queryFn: () => api.lire<Rapport[]>("/v1/rapports?pageSize=50"),
  });
}

export function useModelesDisponibles(type: string) {
  return useQuery({
    queryKey: ["rapports", "modeles", type],
    queryFn: () =>
      api.lire<ModeleExportSelectionnable[]>(`/v1/rapports/${encodeURIComponent(type)}/modeles`),
    enabled: Boolean(type),
  });
}

export function useSuiviRapport(rapportId: string | null) {
  return useQuery({
    queryKey: ["rapports", rapportId],
    queryFn: () => api.lire<Rapport>(`/v1/rapports/${encodeURIComponent(rapportId!)}`),
    enabled: rapportId !== null,
    refetchInterval: (requete) =>
      requete.state.data?.data.statut === "en_cours" ? 3_000 : false,
  });
}

export function useDemanderRapport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ type, corps }: { type: string; corps: DemandeRapport }) =>
      api.creer<Rapport>(`/v1/rapports/${encodeURIComponent(type)}/generer`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["rapports"] });
    },
  });
}

export function useUrlDeTelechargementRapport() {
  return useMutation({
    mutationFn: (rapportId: string) =>
      api.lire<{ url_telechargement: string }>(
        `/v1/rapports/${encodeURIComponent(rapportId)}/download`,
      ),
  });
}

export function useSupprimerRapport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (rapportId: string) =>
      api.supprimer(`/v1/rapports/${encodeURIComponent(rapportId)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["rapports"] });
    },
  });
}
